import { ResponseLoyaltyMascotInner } from "queries/useGetLoyaltyMascots";
import { GameType } from "types/Games";
import {
  LoyaltyCardRewardContent,
  LoyaltyCardRewardProps,
  LoyaltyCardRewardVariant,
} from "components/atoms/LoyaltyCardReward/types";
import {
  CurrencyRewardLoyalty,
  LevelRankLoyalty,
  Loyalty,
  LoyaltyRewardUser,
  LoyaltyRewardUserStatus,
  RankLoyalty,
} from "types/Loyalty";
import {
  RewardBonusNominal,
  RewardCashback,
  RewardFreeSpins,
} from "types/Reward";
import dayjs from "dayjs";
import { BoosterApi, BoosterBackend, BoosterStatus } from "../types/Booster";
import { CashbackResponseInner, CashbackVariant } from "../types/Cashback";
import {
  LoyaltyRewardMiniCardPropsWithId,
  LoyaltyRewardMiniCardVariant,
} from "../components/molecules/LoyaltyRewardMiniCard/types";
import { BodyAssignCashback } from "../queries/useAssignCashback";
import { MutationRewardAssignBody } from "../queries/useLoyaltyRewardAssign";

export type InfoRank = { indexRank: number; indexLevel?: number };

const checkInfoRewardRankLevel = (
  infoReward: LoyaltyRewardUser,
  infoRank: InfoRank,
) =>
  infoReward?.rankIndex
    ? // eslint-disable-next-line sonarjs/no-identical-expressions
      infoReward.rankIndex === infoRank.indexRank &&
      infoReward.rankIndex === infoRank.indexRank
    : infoReward.rankIndex === infoRank.indexRank;

const formatNominal = (
  id: string,
  access: boolean,
  userRewards: LoyaltyRewardUser[],
  infoRewards: Array<RewardFreeSpins | RewardBonusNominal | RewardCashback>,
  currency: string,
  infoRank: InfoRank,
): LoyaltyCardRewardProps => {
  const idLocal = Math.random().toString();

  const infoReward = infoRewards.find((reward) => reward.reward.id === id) as
    | RewardBonusNominal
    | undefined;

  const amountCurrency = infoReward?.rewardNominal.amount.find(
    (amo) => amo.currency,
  )?.value;

  if (!access) {
    return {
      id: Math.random().toString(),
      variant: "cashBonus",
      content: "info",
      value: amountCurrency ? `${amountCurrency / 100} ${currency}` : undefined,
    };
  }

  const userReward = userRewards.find(
    (reward) =>
      reward.rewardId === id && checkInfoRewardRankLevel(reward, infoRank),
  );

  if (userReward) {
    const content: LoyaltyCardRewardContent =
      userReward.status === "assigned" ? "completed" : "progress";

    return {
      id: Math.random().toString(),
      content,
      variant: "cashBonus",
      value: amountCurrency ? `${amountCurrency / 100} ${currency}` : undefined,
    };
  }

  return {
    id: idLocal,
    variant: "cashBonus",
    content: "info",
  };
};

const formatFreeSpins = (
  id: string,
  access: boolean,
  userRewards: LoyaltyRewardUser[],
  infoRewards?: Array<RewardFreeSpins | RewardBonusNominal | RewardCashback>,
  gamesInfo?: GameType[],
): LoyaltyCardRewardProps => {
  const localId = Math.random().toString();

  const infoFreeSpinReward = infoRewards?.find(
    (rew) => rew.reward.id === id,
  ) as RewardFreeSpins | undefined;

  const countFreeSpins = infoFreeSpinReward
    ? infoFreeSpinReward?.rewardFreeSpin.freeSpinsCount
    : undefined;

  const idGameFreeSpin = infoFreeSpinReward?.rewardFreeSpin?.freeSpinsGame;
  const gameFreeSpin = gamesInfo?.find((game) => game.uuid === idGameFreeSpin);

  const defaultCard: LoyaltyCardRewardProps = {
    id: localId,
    variant: "freeSpins",
    content: "info",
    value: countFreeSpins !== undefined ? `${countFreeSpins} FS` : "",
    subtitleValue: gameFreeSpin?.name,
  };

  if (!access) {
    return defaultCard;
  }

  const content: LoyaltyCardRewardContent =
    userRewards?.find((rew) => rew.rewardId === id)?.status === "assigned"
      ? "completed"
      : "progress";

  return {
    ...defaultCard,
    content,
  };
};

const formatBooster = (
  id: string,
  access: boolean,
  boostersUser: BoosterApi[],
  games: GameType[],
  boostersInfo: BoosterBackend[],
): LoyaltyCardRewardProps => {
  const infoBooster = boostersInfo.find((booster) => booster.id === id);
  const currentGame = games.find((game) => game.uuid === infoBooster?.gameId);

  const defaultCard: LoyaltyCardRewardProps = {
    id,
    variant: "booster",
    value: infoBooster ? `x${infoBooster.multiplier}` : undefined,
    subtitleValue: currentGame ? currentGame.name : undefined,
    content: "info",
  };

  if (!access) {
    return defaultCard;
  }

  const boosterCurrent = boostersUser.find(
    (booster) => booster.boosterId === id,
  );

  if (!boosterCurrent) {
    return defaultCard;
  }

  const isProgress =
    boosterCurrent.status === BoosterStatus.Available ||
    boosterCurrent.status === BoosterStatus.Active;

  return {
    ...defaultCard,
    content: isProgress ? "progress" : "completed",
    subtitleValue: currentGame?.name ? currentGame.name : undefined,
  };
};
const formatMascot = (access: boolean): LoyaltyCardRewardProps => {
  const defaultCard: LoyaltyCardRewardProps = {
    id: Math.random().toString(),
    variant: "avatar",
    content: "info",
  };

  if (!access) {
    return defaultCard;
  }

  return {
    ...defaultCard,
    content: "completed",
  };
};

const formatCashback = (
  idCashback: string,
  access: boolean,
  infoRewards: Array<RewardFreeSpins | RewardBonusNominal | RewardCashback>,
): LoyaltyCardRewardProps => {
  const cashback = infoRewards.find(
    (reward) =>
      reward.reward.id === idCashback && reward.reward.variant === "cashback",
  ) as RewardCashback | undefined;
  const variants: Record<CashbackVariant, LoyaltyCardRewardVariant> = {
    [CashbackVariant.DAILY]: "dailyCashback",
    [CashbackVariant.WEEKLY]: "weeklyCashback",
    [CashbackVariant.MONTHLY]: "monthlyCashback",
  };
  const cashbackVariant = cashback
    ? cashback.rewardCashback.cashbackVariant
    : undefined;

  const defaultCashback: LoyaltyCardRewardProps = {
    id: Math.random().toString(),
    content: "info",
    variant: cashbackVariant ? variants[cashbackVariant] : "dailyCashback",
    value: cashback ? `${cashback?.rewardCashback.percent}%` : "",
  };

  if (!access) {
    return defaultCashback;
  }

  defaultCashback.content = "progress";

  return defaultCashback;
};

const getCurrencyRewards = (
  currenciesRewards: CurrencyRewardLoyalty[],
  currencyUser: string,
) => {
  const defaultRewards = {
    mascot: null,
    booster: null,
    freeSpin: null,
  };

  if (!currenciesRewards) {
    return defaultRewards;
  }

  const rewards = currenciesRewards.find(
    (currencyReward) => currencyReward.currencyCode === currencyUser,
  );

  if (!rewards) {
    return defaultRewards;
  }

  return {
    booster: rewards?.currencyReward?.boosterId,
    mascot: rewards?.currencyReward?.mascotId,
    freeSpin: rewards?.currencyReward?.rewardFreeSpinId,
  };
};

const getRankRewards = (rankRewards: LevelRankLoyalty["rewards"]) => {
  return {
    nominal: rankRewards?.rewardNominalId,
    deposit: rankRewards?.rewardDepositWeeklyId,
    cashbackDaily: rankRewards?.rewardCashbackDailyId,
    cashbackWeekly: rankRewards?.rewardCashbackWeeklyId,
    cashbackMonthly: rankRewards?.rewardCashbackMonthlyId,
  };
};

export const getIdsRewards = (
  loyalty?: Loyalty,
  currencyUser?: string,
  infoRank?: { nameRank: string; level?: number },
) => {
  const freeSpins: string[] = [];
  const nominals: string[] = [];
  const boosters: string[] = [];
  const deposits: string[] = [];
  const mascots: string[] = [];
  const cashbackDaily: string[] = [];
  const cashbackWeekly: string[] = [];
  const cashbackMonthly: string[] = [];

  if (!loyalty || !currencyUser) {
    return {
      freeSpins,
      nominals,
      boosters,
      deposits,
      mascots,
      cashbackDaily,
      cashbackWeekly,
      cashbackMonthly,
    };
  }

  if (infoRank) {
    const rank = loyalty.settings.ranks.find(
      (rankInner) => rankInner.name === infoRank.nameRank,
    )!;

    if (infoRank.level) {
      const level = rank.levels.find(
        (levelInner) => Number(levelInner.name) === Number(infoRank.level),
      );

      const { mascot, freeSpin, booster } = getCurrencyRewards(
        level?.rewards?.currencyRewards ?? [],
        currencyUser,
      );

      if (mascot) {
        mascots.push(mascot);
      }

      if (freeSpin) {
        freeSpins.push(freeSpin);
      }

      if (booster) {
        boosters.push(booster);
      }

      if (level?.rewards.rewardNominalId) {
        nominals.push(level?.rewards.rewardNominalId);
      }

      return {
        mascots: [...new Set(mascots)],
        freeSpins: [...new Set(freeSpins)],
        nominals: [...new Set(nominals)],
        boosters: [...new Set(boosters)],
        deposits: [...new Set(deposits)],
        cashbackDaily: [...new Set(cashbackDaily)],
        cashbackWeekly: [...new Set(cashbackWeekly)],
        cashbackMonthly: [...new Set(cashbackMonthly)],
      };
    }

    const {
      nominal,
      deposit,
      cashbackDaily: cashbackDailyRank,
      cashbackWeekly: cashbackWeeklyRank,
      cashbackMonthly: cashbackMonthlyRank,
    } = getRankRewards(rank.rewards);

    const { booster, mascot, freeSpin } = getCurrencyRewards(
      rank.rewards.currencyRewards,
      currencyUser,
    );

    if (nominal) {
      nominals.push(nominal);
    }

    if (deposit) {
      deposits.push(deposit);
    }

    if (cashbackDailyRank) {
      cashbackDaily.push(cashbackDailyRank);
    }

    if (cashbackWeeklyRank) {
      cashbackWeekly.push(cashbackWeeklyRank);
    }

    if (cashbackMonthlyRank) {
      cashbackMonthly.push(cashbackMonthlyRank);
    }

    if (booster) {
      boosters.push(booster);
    }

    if (freeSpin) {
      freeSpins.push(freeSpin);
    }

    if (mascot) {
      mascots.push(mascot);
    }

    return {
      mascots: [...new Set(mascots)],
      freeSpins: [...new Set(freeSpins)],
      nominals: [...new Set(nominals)],
      boosters: [...new Set(boosters)],
      deposits: [...new Set(deposits)],
      cashbackDaily: [...new Set(cashbackDaily)],
      cashbackWeekly: [...new Set(cashbackWeekly)],
      cashbackMonthly: [...new Set(cashbackMonthly)],
    };
  }

  loyalty.settings.ranks.forEach((rank) => {
    const {
      nominal,
      deposit,
      cashbackDaily: cashbackDailyRank,
      cashbackWeekly: cashbackWeeklyRank,
      cashbackMonthly: cashbackMonthlyRank,
    } = getRankRewards(rank.rewards);

    const { booster, mascot, freeSpin } = getCurrencyRewards(
      rank.rewards.currencyRewards,
      currencyUser,
    );

    if (nominal) {
      nominals.push(nominal);
    }
    if (deposit) {
      deposits.push(deposit);
    }
    if (cashbackDailyRank) {
      cashbackDaily.push(cashbackDailyRank);
    }
    if (cashbackWeeklyRank) {
      cashbackDaily.push(cashbackWeeklyRank);
    }
    if (cashbackMonthlyRank) {
      cashbackMonthly.push(cashbackMonthlyRank);
    }
    if (booster) {
      boosters.push(booster);
    }
    if (mascot) {
      mascots.push(mascot);
    }
    if (freeSpin) {
      freeSpins.push(freeSpin);
    }

    rank.levels.forEach((level) => {
      const {
        mascot: mascotLevel,
        freeSpin: freeSpinLevel,
        booster: boosterLevel,
      } = getCurrencyRewards(level.rewards.currencyRewards, currencyUser);

      const {
        nominal: nominalLevel,
        deposit: depositLevel,
        cashbackDaily: cashbackDailyLevel,
        cashbackWeekly: cashbackWeeklyLevel,
        cashbackMonthly: cashbackMonthlyLevel,
      } = getRankRewards(level.rewards);

      if (nominalLevel) {
        nominals.push(nominalLevel);
      }
      if (depositLevel) {
        deposits.push(depositLevel);
      }
      if (cashbackDailyLevel) {
        cashbackDaily.push(cashbackDailyLevel);
      }
      if (cashbackWeeklyLevel) {
        cashbackDaily.push(cashbackWeeklyLevel);
      }
      if (cashbackMonthlyLevel) {
        cashbackMonthly.push(cashbackMonthlyLevel);
      }
      if (boosterLevel) {
        boosters.push(boosterLevel);
      }
      if (mascotLevel) {
        mascots.push(mascotLevel);
      }
      if (freeSpinLevel) {
        freeSpins.push(freeSpinLevel);
      }
    });
  });

  return {
    mascots: [...new Set(mascots)],
    freeSpins: [...new Set(freeSpins)],
    nominals: [...new Set(nominals)],
    boosters: [...new Set(boosters)],
    deposits: [...new Set(deposits)],
    cashbackDaily: [...new Set(cashbackDaily)],
    cashbackWeekly: [...new Set(cashbackWeekly)],
    cashbackMonthly: [...new Set(cashbackMonthly)],
  };
};

type GetRewardsOnRankParams = {
  indexLevel?: number;
  nameRank?: string;
  loyalty?: Loyalty;
  userExperience?: number;
  userRewards?: LoyaltyRewardUser[];
  infoRewards?: Array<RewardFreeSpins | RewardBonusNominal | RewardCashback>;
  gamesInfo?: GameType[];
  currencyCode?: string;
  mascots?: ResponseLoyaltyMascotInner[];
  boostersUser?: BoosterApi[];
  boostersInfo?: BoosterBackend[];
};

export const getRewardsOnRank = (
  params: GetRewardsOnRankParams,
): LoyaltyCardRewardProps[] => {
  const {
    loyalty,
    userExperience,
    nameRank,
    userRewards,
    infoRewards,
    currencyCode,
    gamesInfo,
    boostersUser,
    boostersInfo,
    mascots,
    indexLevel = undefined,
  } = params;

  if (
    !loyalty ||
    userExperience === undefined ||
    !nameRank ||
    !userRewards ||
    !infoRewards ||
    !currencyCode ||
    !gamesInfo ||
    !boostersUser ||
    !boostersInfo ||
    !mascots
  ) {
    return [];
  }

  const indexRank = loyalty.settings.ranks.findIndex(
    (rank) => rank.name === nameRank,
  );

  if (indexRank === undefined) {
    return [];
  }

  const infoRank: InfoRank = {
    indexRank,
    indexLevel: indexLevel ? indexLevel + 1 : undefined,
  };

  const target: RankLoyalty | LevelRankLoyalty | undefined =
    indexLevel !== undefined
      ? loyalty.settings.ranks.find((rank) => rank.name === nameRank)?.levels?.[
          indexLevel
        ]
      : loyalty.settings.ranks.find((rank) => rank.name === nameRank);

  if (!target) {
    return [];
  }

  const cards: LoyaltyCardRewardProps[] = [];
  const targetIsReached =
    "levels" in target
      ? userExperience >= target?.levels?.[0].requirement
      : userExperience >= target.requirement;

  if (target.rewards?.rewardNominalId) {
    cards.push(
      formatNominal(
        target.rewards.rewardNominalId,
        targetIsReached,
        userRewards,
        infoRewards,
        currencyCode,
        infoRank,
      ),
    );
  }

  if (target.rewards?.rewardCashbackDailyId) {
    cards.push(
      formatCashback(
        target.rewards.rewardCashbackDailyId,
        targetIsReached,
        infoRewards,
      ),
    );
  }

  if (target.rewards?.rewardCashbackWeeklyId) {
    cards.push(
      formatCashback(
        target.rewards.rewardCashbackWeeklyId,
        targetIsReached,
        infoRewards,
      ),
    );
  }

  if (target.rewards?.rewardCashbackMonthlyId) {
    cards.push(
      formatCashback(
        target.rewards.rewardCashbackMonthlyId,
        targetIsReached,
        infoRewards,
      ),
    );
  }

  const currencyRewards = target.rewards.currencyRewards
    ? target.rewards.currencyRewards.find(
        (rew) => rew.currencyCode === currencyCode,
      )?.currencyReward
    : undefined;

  if (currencyRewards) {
    if (currencyRewards.rewardFreeSpinId) {
      cards.push(
        formatFreeSpins(
          currencyRewards.rewardFreeSpinId,
          targetIsReached,
          userRewards,
          infoRewards,
          gamesInfo,
        ),
      );
    }

    if (currencyRewards.boosterId) {
      cards.push(
        formatBooster(
          currencyRewards.boosterId,
          targetIsReached,
          boostersUser,
          gamesInfo,
          boostersInfo,
        ),
      );
    }

    if (currencyRewards.mascotId) {
      cards.push(formatMascot(targetIsReached));
    }
  }

  return cards;
};

type FormatLoyaltyRewardsUserParams = {
  loyalty: Loyalty;
  userRewards: LoyaltyRewardUser[];
  freeSpinsGames: GameType[];
  infoRewards: Array<RewardFreeSpins | RewardBonusNominal>;
  assignReward: (body: MutationRewardAssignBody) => void;
  assignCashback: (body: BodyAssignCashback) => void;
  currency?: string;
  dailyCashback?: CashbackResponseInner;
  weeklyCashback?: CashbackResponseInner;
  monthlyCashback?: CashbackResponseInner;
};

export const formatLoyaltyRewardsUser = (
  params: FormatLoyaltyRewardsUserParams,
): {
  nominals: LoyaltyRewardMiniCardPropsWithId[];
  cashback: LoyaltyRewardMiniCardPropsWithId[];
  freeSpins: LoyaltyRewardMiniCardPropsWithId[];
} => {
  const {
    loyalty,
    userRewards,
    infoRewards,
    currency,
    freeSpinsGames,
    assignCashback,
    assignReward,
    dailyCashback,
    weeklyCashback,
    monthlyCashback,
  } = params;

  const nominals: Array<{
    reward: LoyaltyRewardUser;
    info: RewardBonusNominal;
  }> = [];
  const freeSpins: Array<{ reward: LoyaltyRewardUser; info: RewardFreeSpins }> =
    [];

  const availableRewards = userRewards.filter(
    (rew) => rew.status === LoyaltyRewardUserStatus.Available,
  );

  for (let i = 0; i < availableRewards.length; i += 1) {
    const idAvailableReward = availableRewards[i].rewardId;
    const info = infoRewards.find(
      (infoReward) => idAvailableReward === infoReward.reward.id,
    );

    if (info && info.reward.variant === "free_spins") {
      freeSpins.push({
        reward: availableRewards[i],
        info: info as RewardFreeSpins,
      });
    }

    if (info && info.reward.variant === "bonus_nominal") {
      nominals.push({
        reward: availableRewards[i],
        info: info as RewardBonusNominal,
      });
    }
  }

  const formatNominalCardProps = (
    reward: {
      reward: LoyaltyRewardUser;
      info: RewardBonusNominal;
    },
    index: number,
  ): LoyaltyRewardMiniCardPropsWithId => {
    const isFirst = index === 0;
    const amountCurrency = currency
      ? reward.info.rewardNominal.amount.find(
          (amo) => amo.currency === currency,
        )?.value
      : "";
    const rankName =
      loyalty?.settings?.ranks[reward.reward.rankIndex].name ?? "";
    const level = reward.reward.levelIndex;
    return {
      id: reward.reward.id,
      variant: LoyaltyRewardMiniCardVariant.NOMINAL,
      canGet: isFirst,
      primaryTitle: isFirst,
      image:
        "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/cash-bonus-merged.webp",
      value: amountCurrency ? `${String(Number(amountCurrency) / 100)}` : "",
      handleClick: () =>
        assignReward({
          loyaltyRewardId: reward.reward.id,
          rewardId: reward.reward.rewardId,
        }),
      rankName: `${rankName} ${level ? level + 1 : ""}`,
    };
  };

  const formatCashbackCardProps = (
    cashback?: CashbackResponseInner,
  ): LoyaltyRewardMiniCardPropsWithId | undefined => {
    if (!cashback) {
      return undefined;
    }
    const { id, variant, percent, minCashback } = cashback.cashback;

    const map = {
      [CashbackVariant.DAILY]: LoyaltyRewardMiniCardVariant.CASHBACK_DAILY,
      [CashbackVariant.WEEKLY]: LoyaltyRewardMiniCardVariant.CASHBACK_WEEKLY,
      [CashbackVariant.MONTHLY]: LoyaltyRewardMiniCardVariant.CASHBACK_MONTHLY,
    };

    const defaultProps: LoyaltyRewardMiniCardPropsWithId = {
      id,
      variant: map[variant],
      value: percent ? `${percent}%` : "",
      image:
        variant === CashbackVariant.WEEKLY
          ? "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/weekly-cashback-merged.webp"
          : "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/daily-cashback-merged.webp",
    };

    const rankName = cashback?.loyaltyReward?.rankIndex
      ? loyalty?.settings?.ranks[cashback?.loyaltyReward?.rankIndex].name
      : "";

    if (!currency) {
      return defaultProps;
    }

    const { value } = cashback.cashbackAccumulation;
    const { id: loyaltyRewardId } = cashback.loyaltyReward;
    const currentDayWeek = dayjs().day();
    const currentDayMonth = dayjs().date();
    const minCashbackValueCurrency = minCashback[currency] as number | string;
    const formattedValue = (value / 100) * percent;
    const valueIsAccumulated =
      formattedValue >= Number(minCashbackValueCurrency);
    const canGet =
      (valueIsAccumulated && variant === CashbackVariant.DAILY) ||
      (valueIsAccumulated &&
        variant === CashbackVariant.WEEKLY &&
        currentDayWeek === 0) ||
      (valueIsAccumulated &&
        variant === CashbackVariant.MONTHLY &&
        currentDayMonth === 0);

    return {
      ...defaultProps,
      canGet,
      primaryTitle: canGet,
      rankName: cashback?.loyaltyReward?.rankIndex
        ? `${rankName} ${cashback?.loyaltyReward?.rankIndex}`
        : rankName,
      handleClick: () => {
        assignCashback({
          loyaltyRewardId,
        });
      },
    };
  };

  const formatFreeSpinCardProps = (
    freeSpin: {
      reward: LoyaltyRewardUser;
      info: RewardFreeSpins;
    },
    index: number,
  ): LoyaltyRewardMiniCardPropsWithId => {
    const { id, rewardId } = freeSpin.reward;
    const { freeSpinsCount, freeSpinsGame } = freeSpin.info.rewardFreeSpin;
    const isFirst = index === 0;
    const rankName =
      loyalty?.settings?.ranks[freeSpin.reward.rankIndex].name ?? "";
    const level = freeSpin.reward.levelIndex;
    const game = freeSpinsGames.find((g) => g.uuid === freeSpinsGame);

    return {
      id,
      variant: LoyaltyRewardMiniCardVariant.FREE_SPINS,
      value: String(freeSpinsCount),
      canGet: isFirst,
      primaryTitle: isFirst,
      image: game
        ? `https://diswdgcu9cfva.cloudfront.net/covers2/${game.urlAlias}.webp`
        : "https://diswdgcu9cfva.cloudfront.net/loyalty/reward/slots.webp",
      rankName: `${rankName} ${level ? level + 1 : ""}`,
      handleClick: () =>
        assignReward({
          loyaltyRewardId: id,
          rewardId,
        }),
    };
  };

  return {
    nominals: nominals
      .sort(
        (a, b) =>
          new Date(a.reward.createdAt).valueOf() -
          new Date(b.reward.createdAt).valueOf(),
      )
      .map(formatNominalCardProps),
    freeSpins: freeSpins
      .sort(
        (a, b) =>
          new Date(a.reward.createdAt).valueOf() -
          new Date(b.reward.createdAt).valueOf(),
      )
      .map(formatFreeSpinCardProps),
    cashback: [dailyCashback, weeklyCashback, monthlyCashback]
      .filter((cb) => cb !== undefined)
      .map(formatCashbackCardProps) as LoyaltyRewardMiniCardPropsWithId[],
  };
};
